// CustomOrderRequestsPage.js
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { checkDuplicatePhoneCount, fetchCustomOrderRequests, updateOrderStatus, deleteOrder, fetchCustomOrdersSummary } from "../../api/OrderAPIs";
import { checkReturnMatch } from "../../api/ReturnsAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { useAuth } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white shadow-md rounded-lg p-6 mb-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const CustomOrderRequestsPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.4000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.4000");

  const { data: customOrdersSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["customOrdersSummary"],
    queryFn: fetchCustomOrdersSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });

  useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const isLoading = isLoadingSummary;
  const queryError = errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      alert("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: type.name,
            driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          status: order.status || "New",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchCustomOrderRequests(0, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial custom order requests:", err);
      setError("فشل في تحميل طلبات التصاميم المخصصة. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل طلبات التصاميم المخصصة!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchCustomOrderRequests(currentPage, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more custom order requests:", err);
      setError("فشل في تحميل المزيد من طلبات التصاميم المخصصة. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من طلبات التصاميم المخصصة!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus, order) => {
    let reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const phone = `${order.phoneNumber}${order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""}`;
        const formattedState = `${order.state} (C)`;

        incrementReference();

        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);

        setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
        toast.success("تم النسخ!");

      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    }
    else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      console.error("Error deleting order:", err);
      let errorMessage = "فشل في حذف الطلب. يرجى المحاولة مرة أخرى.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleCopyMessage = (order) => {
    const itemsList = order.items.map(item => `النوع: ${item.type} - الكود: ${item.code}`).join('\n');

    const messages = [
      `أهلاً أ/ "${order.name}" 🌸
  مع حضرتك فاطمة من فريق جرابتكم ستور 💛
  بنراجع معاك تفاصيل الطلب قبل ما نبدأ التنفيذ، لأن كل تصميم بيتعمل مخصوص لك 🎨✨

  تفاصيل الطلب:
  ${itemsList}

  بمجرد تأكيدك هنبدأ فورًا في التجهيز والشحن ⚡🚚

  لتأكيد الطلب: رد بـ (نعم - تأكيد) ✅
  لو في تعديل أو إلغاء، قولنا قبل البدء 💬
  شكرًا لثقتك فينا 🤍🌷`,

      // ... (keep all other messages as in the original)
    ];

    const randomMessage = messages[Math.floor(Math.random() * messages.length)];

    copyToClipboard(randomMessage);
  };
  
  const incrementReference = () => {
    const match = currentReference.match(/(\d+)$/);
    if (match) {
      const num = parseInt(match[0], 10);
      const newNum = num + 1;
      const newRef = currentReference.replace(/\d+$/, newNum.toString());
      setCurrentReference(newRef);
      localStorage.setItem("orderReference", newRef);
    }
  };

  const handleCopyAllExcelData = async (order) => {
    try {
      // فحص التكرار
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (C)`;
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items; // دلوقتي فيها التكرار (مثلاً 3 عناصر لو الكمية 3)
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`; // 1-3 , 2-3 , 3-3
        const type = item.type || "Unknown Type";
        const driveLink = item.driveLink || "No Link";

        const itemData = `\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      copyToClipboard(finalText);

      // زيادة الريفرنس بعد النسخ (لو عايز)
      // incrementReference();

      toast.success("تم نسخ الطلب كامل مع التكرار بنجاح!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };


  // ... (keep handleCopyBostaMessage as in the original)
  // ... (keep incrementReference, updateReferenceFromInput, handleMarkAllAsOld as in the original)

  if (queryError) {
    return <div>Error: {queryError.message}</div>;
  }

  return (
    <div className="p-4 max-w-6xl mx-auto" dir="rtl">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">طلبات التصاميم المخصصة</h2>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            رجوع
          </button>
        </div>      <ToastContainer />
      

      {/* ... (keep the handleMarkAllAsOld section, but adapt title to "تحويل الكل إلى قديم فقط" if needed) */}

      {/* ... (keep the reference setup section) */}

      <div className="bg-white shadow-md rounded-lg p-6 ">
        {isOrdersLoading ? (
          <SkeletonLoader type="table" />
        ) : (
          <>
            {loadedOrders.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                <table className="w-full table-auto text-lg">
                <thead>
                  <tr className="bg-gray-100 text-right">
                    <th className="p-2 font-semibold min-w-[100px]">بيانات العميل</th>
                    <th className="p-2 font-semibold min-w-[50px]">رقم</th>
                    <th className="p-2 font-semibold">المنتجات</th>
                    <th className="p-2 font-semibold">رابط الدرايف</th>
                    <th className="p-2 font-semibold">ملاحظات</th>
                    <th className="p-2 font-semibold">نسخ</th>
                    <th className="p-2 font-semibold">إجراءات</th>
                    <th className="p-2 font-semibold">حذف</th>
                  </tr>
                </thead>
                <tbody>
                    {loadedOrders.map((order) => (
                    <tr key={order.orderNumber} className="border-b hover:bg-gray-50">
                      {/* عمود واحد لبيانات العميل */}
                      <td className="p-3 align-top text-xs leading-relaxed">
                        <div className="space-y-1">
                          <div><strong>الاسم:</strong> {order.name}</div>
                          <div><strong>الهاتف الاول: </strong> 
                            <a
                              href={`https://wa.me/${order.phoneNumber.replace(/[^0-9]/g, '').replace(/^0/, '20')}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-green-600 hover:text-green-800 font-large underline"
                            >
                              {order.phoneNumber}
                            </a>
                            </div>
                          <div>
                            <strong>الهاتف التاني: </strong> 
                            <>
                            {order.phoneNumber2 !== "غير معروف" && (
                              <>
                                {' - '}
                                <a
                                  href={`https://wa.me/${order.phoneNumber.replace(/[^0-9]/g, '').replace(/^0/, '20')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-green-600 hover:text-green-800 font-large underline"
                                >
                                  {order.phoneNumber2}
                                </a>
                              </>
                            )}
                            </>
                          </div>
                          <div><strong>العنوان:</strong> {order.address}</div>
                          <div><strong>المحافظة:</strong> {order.state}</div>
                          <div><strong>الإجمالي:</strong> LE {order.total}</div>
                          <div><strong>التاريخ:</strong> {order.date}</div>
                        </div>
                      </td>

                      {/* رقم المنتج */}
                      <td className="p-2 text-center">
                        {order.items.map((item, index) => (
                          <div key={index} className="mb-1">
                            {item.quantityIndex}-{item.totalQuantity}
                          </div>
                        ))}
                      </td>

                      {/* المنتجات */}
                      <td className="p-2 text-right">
                        {order.items.map((item) => (
                          <div key={item.id} className="mb-3 text-xs">
                            <div className="text-sm"><strong>{item.name}</strong> ({item.type})</div>
                          </div>
                        ))}
                      </td>

                      {/* رابط الدرايف */}
                      <td className="p-2 text-right text-xs">
                        {order.items.map((item) => (
                          <div key={item.id} className="mb-3">
                            {item.driveLink ? (
                              <>
                                <a href={item.driveLink} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                                  رابط
                                </a>
                                {item.code.startsWith("CUSTOM") && (
                                  <button
                                    onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                    className="block mt-1 bg-green-600 text-white text-xs px-2 py-1 rounded hover:bg-green-700"
                                  >
                                    تحميل
                                  </button>
                                )}
                                <div className="text-gray-500 text-sm">{item.code}</div>
                              </>
                            ) : (
                              <span className="text-gray-400">لا يوجد</span>
                            )}
                          </div>
                        ))}
                      </td>

 

                      {/* الملاحظات */}
                      <td className="p-2 text-right">{order.note || "لا توجد ملاحظات"}</td>

                      {/* نسخ */}
                      {authState.role === "Admin" && (
                        <td className="p-2 text-right">
                          <div className="flex flex-col space-y-1">
                            <button
                              onClick={() => handleCopyAllExcelData(order)}
                              className="bg-green-500 text-white rounded hover:bg-green-600 text-sm px-3 py-8 font-medium"
                            >
                              نسخ المعلومات 
                            </button>
                          </div>
                        </td>
                      )}
                          
                         
                          <td className="p-2 text-right">
                            <div className="flex flex-col space-y-1">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 text-sm font-bold"
                              >
                                تحديث إلى قديم
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white rounded hover:bg-yellow-600 text-sm px-3 py-2 font-medium"
                              >
                                على الانتظار
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="bg-purple-500 text-white rounded hover:bg-purple-600 text-sm px-3 py-2 font-medium"
                              >
                                تحديث إلى تغييرات
                              </button>

                             
                            </div>
                          </td>
                          <td className="p-2 text-right">
                            {(authState.role === "Admin" || authState.role === "Moderator") && (
                              <div className="flex flex-col space-y-1">
                                <button
                                  onClick={() => handleDeleteOrder(order.orderNumber)}
                                  className="bg-red-500 text-white rounded hover:bg-red-600 text-sm px-3 py-2 font-medium w-full"
                                >
                                  حذف
                                </button>
                                <button
                                  onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                  className="bg-red-500 text-white rounded hover:bg-red-600 text-sm px-3 py-2 font-medium"
                                >
                                  معندهوش واتساب او مردش - Spam
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                    ))}
                    </tbody>
                  </table>
                  {hasMore && (
                    <div className="text-center mt-4">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                      >
                        {isLoadingMore ? "جاري التحميل..." : "تحميل المزيد"}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <p className="text-center">لا توجد طلبات تصاميم مخصصة</p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomOrderRequestsPage;